// AssetLightbox.jsx — 文档内资产的全景弹窗（第五十二批；第五十六批 S1/S2 手机修复）。
//
// 用途：表格里的 1:1 缩略图点开后，在这里按视口自适应铺满看全貌；需要抠细节时
// 滚轮/双击/双指捏合以指（标）为锚点局部放大、拖拽平移，一键还原回适应视口。
// 视频同样在此播放。缩放/平移的数学全部在 pages/assetView.mjs（纯函数、有单测），
// 本组件只管交互与绘制。
//
// 第五十六批修的两处手机死局（判据与病灶原文都在 assetView.mjs 对应导出上方）：
//   S1 头部不换行 ⇒ 「关闭」被顶出 360px 屏外，而全屏 Dialog 无遮罩可点、手机无
//      Esc 键、返回键会离开 /docs —— 点开任意一张图只能刷新页面才能出来。
//      现在：关闭钉在标题行；工具条自换行；空白处点击=点遮罩；返回键先关弹窗。
//   S2 touchAction:'none' 掐掉了原生捏合却没有任何触屏缩放替代 —— 现补两指捏合。
//
// 本批（评审 D-A/D-B/D-C）继续修三处（判据同样在 assetView.mjs 对应导出上方）：
//   D-A 捏合的起手快照只在 pointerdown 拍一次，而"当前指对"每帧重取 —— 三指按住
//       抬走其中一根，两者会换成两对不同的手指（syncPinch）。
//   D-B 「透明区提示」在弹窗里是死控件：挂的 class 全仓无对应 CSS（alphaHintSx）。
//   D-C React 的 onWheel 是被动监听，preventDefault 无效，触控板捏合会连页面一起
//       缩放 —— 改为自己挂 { passive: false } 的 wheel（wheelZoomIntent）。
//
// 依赖纪律：本项目不装 @mui/icons-material，控件一律用等宽文本字形（见 DocBrowserPage
// 的 caret 同款约定）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  IDENTITY_VIEW, LIGHTBOX_TOOLS_PROPS, alphaHintSx, backSentinelState, clampPan, exceedsTapSlop,
  gestureOf, isIdentityView, makeBackToken, nextZoom, pinchView, shouldCloseOnPop,
  shouldRewindOnUnmount, syncPinch, wheelZoomIntent, zoomAround,
} from '../pages/assetView.mjs';
import { useT } from '../i18n/index.mjs';

export default function AssetLightbox({ open, src, alt, kind, hint, onToggleHint, onClose }) {
  const t = useT();
  const [view, setView] = useState(IDENTITY_VIEW);
  const boxRef = useRef(null);
  const wheelBoundRef = useRef(null);    // 实际挂在节点上的那个转发函数（解绑时要用同一个）
  const dragRef = useRef(null);
  const ptsRef = useRef(new Map());      // pointerId → 客户端坐标（活跃指针）
  const pinchRef = useRef(null);         // { pairKey, start, startView } 捏合起手快照
  const movedRef = useRef(false);        // 本次手势有没有真的动过（区分"点空白"与"手势收尾"）

  // 手势路径必须读"此刻的视图"：setView 是异步的，而重新起手时要拿当前视图当基准
  // （D-A），基准若落后一帧，接手的那一下就是一次瞬移。故 ref 为准、state 只管渲染，
  // 所有写入统一走 commitView —— 绕过它直接 setView 就会让两者分家。
  const viewRef = useRef(IDENTITY_VIEW);
  const commitView = useCallback((next) => {
    const v = typeof next === 'function' ? next(viewRef.current) : next;
    viewRef.current = v;
    setView(v);
  }, []);

  // 每次换图都回到"适应视口"，否则上一张的放大倍率会跟过来（看起来像坏了）。
  useEffect(() => { commitView(IDENTITY_VIEW); }, [src, commitView]);

  // onClose 走 ref：下面那条 popstate 监听只在 open 变化时重挂，不该因为父组件
  // 每渲染一次就新建一个回调而反复插拔历史哨兵。
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  // 返回键即"关闭弹窗"（判据 shouldCloseOnPop / shouldRewindOnUnmount，见 assetView.mjs）。
  // 压入的哨兵与当前 URL 相同，故 react-router 的路由不变、正文不会重取。
  useEffect(() => {
    if (!open) return undefined;
    // 每次打开发一枚唯一令牌：上一次关闭遗留在"前进方向"的孤儿哨兵形状与本次
    // 一模一样，只判形状会让读者按返回时弹窗根本不关（D5a，病灶原文在 assetView.mjs）。
    const token = makeBackToken();
    // 摊平当前 state 而不是压裸对象：react-router 的 idx 记账一断就再也接不上（D5b）。
    window.history.pushState(backSentinelState(window.history.state, token), '');
    const onPop = () => { if (shouldCloseOnPop(window.history.state, token)) closeRef.current?.(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // 不是被返回键关的（按了关闭/Esc/点了空白）⇒ 把自己压的那条哨兵吃掉，
      // 否则读者此后要按两次返回才能真的离开本页。反之，若此刻站的已经不是自己
      // 那条（返回键刚把它消耗掉，落点是页面本体或别人的孤儿），再 back() 会把
      // 读者多送走一步、直接弹出 /docs。
      if (shouldRewindOnUnmount(window.history.state, token)) window.history.back();
    };
  }, [open]);

  const viewport = () => {
    const el = boxRef.current;
    return el ? { viewW: el.clientWidth, viewH: el.clientHeight } : { viewW: 0, viewH: 0 };
  };

  // 客户端坐标 → 相对视口中心的偏移（assetView.mjs 全套数学的坐标约定）。
  const rel = (clientX, clientY) => {
    const el = boxRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: clientX - (r.left + r.width / 2), y: clientY - (r.top + r.height / 2) };
  };

  const applyZoom = useCallback((direction, clientX, clientY) => {
    commitView((cur) => {
      const el = boxRef.current;
      if (!el) return cur;
      const origin = Number.isFinite(clientX) ? rel(clientX, clientY) : { x: 0, y: 0 };
      const z = nextZoom(cur.zoom, direction);
      if (z === cur.zoom) return cur;
      const moved = zoomAround({ pan: cur.pan, zoom: cur.zoom, nextZoomValue: z, origin });
      return { zoom: moved.zoom, pan: clampPan(moved.pan, { zoom: moved.zoom, ...viewport() }) };
    });
  }, [commitView]);

  // 滚轮自己挂，不走 React 的 onWheel：React 18 把 wheel 注册成**被动**监听，
  // 里面的 preventDefault 永远无效（每滚一格控制台报一次 "Unable to preventDefault
  // inside passive event listener invocation"），而触控板捏合是以 ctrl+wheel 到达的
  // ——拦不住，浏览器就会在弹窗底下把整个页面也缩放一遍。判据见 wheelZoomIntent。
  //
  // ⚠ **必须用回调 ref，不能在 useEffect 里读 boxRef.current**（第五十九批实证的病灶）。
  // 第五十八批那版写的是 `useEffect(() => { const el = boxRef.current; ... },
  // [open, kind, applyZoom])`，而 MUI 的 Portal **首帧返回 null**、节点是在它自己的
  // layout effect 之后那一帧才挂上的。于是 open 翻 true 的那一帧 el 仍是 null、effect
  // 提前返回；下一帧节点到位了，可 [open, kind, applyZoom] **一个都没变**，effect 不
  // 再跑——监听器就此永不挂载。
  //
  // 症状极具欺骗性：指针事件与 ± 按钮都是 React 属性、由 React 自己接线，照常工作；
  // 唯独手工挂的这一条失效。需求方的报告正是「滚轮什么都不发生，但 ± 按钮能缩放」。
  // 教训一般化：**任何"在 effect 里读 ref 再挂原生监听"的写法，都隐含了一个没人写下
  // 来的假设——节点在该 effect 跑之前就已经到位。** 跨 Portal / 跨 Suspense / 跨过渡
  // 组件时这个假设都不成立，而回调 ref 是精确的（节点挂上时调用、卸下时以 null 调用），
  // 根本不需要那个假设。
  const wheelHandlerRef = useRef(null);
  wheelHandlerRef.current = (e) => {
    const { handled, direction } = wheelZoomIntent({ kind, deltaY: e.deltaY });
    if (!handled) return;                 // 不接管就不拦：ctrl+滚轮缩页面是无障碍能力
    e.preventDefault();
    applyZoom(direction, e.clientX, e.clientY);
  };
  // 挂在容器上而不是图片上：留白区滚轮同样该缩放（与改前的作用域一致）。
  // 转发给 wheelHandlerRef 而不是直接挂当前闭包：这样 kind/applyZoom 变了也不必
  // 解绑重绑，回调 ref 只在**节点真的换了**的时候触发一次。
  const attachBox = useCallback((el) => {
    if (boxRef.current === el) return;
    if (boxRef.current && wheelBoundRef.current) {
      boxRef.current.removeEventListener('wheel', wheelBoundRef.current);
      wheelBoundRef.current = null;
    }
    boxRef.current = el;
    if (el) {
      wheelBoundRef.current = (e) => wheelHandlerRef.current?.(e);
      el.addEventListener('wheel', wheelBoundRef.current, { passive: false });
    }
  }, []);

  const onDoubleClick = (e) => {
    if (kind !== 'image') return;
    // 已放大 ⇒ 双击还原；未放大 ⇒ 以光标为锚放大一档。
    if (!isIdentityView(viewRef.current)) commitView(IDENTITY_VIEW);
    else applyZoom(+1, e.clientX, e.clientY);
  };

  // 当前手势指对 = 活跃指针 Map 的前两个（Map 保序，即"先落下的两根"）。
  // pointerId 与几何一起返回：起手快照必须知道自己属于**哪一对**手指，否则三指
  // 按住时抬走其中一根会让快照与手势悄悄换成两对人（D-A，判据见 syncPinch）。
  const twoFingerGesture = () => {
    const [a, b] = [...ptsRef.current.entries()].slice(0, 2);
    if (!a || !b) return null;
    return { ids: [a[0], b[0]], gesture: gestureOf([rel(a[1].x, a[1].y), rel(b[1].x, b[1].y)]) };
  };

  // setPointerCapture 对已失效的 pointerId 会抛 NotFoundError（漏收 pointerup 时
  // Map 里可能残留一条），捕获失败不值得让整个手势崩掉。
  const capture = (el, id) => { try { el.setPointerCapture?.(id); } catch { /* 指针已消失 */ } };

  const onPointerDown = (e) => {
    // isPrimary 的 pointerdown = 新一轮手势的第一根手指。此时 Map 里若还有残留
    // （手指在弹窗外抬起，pointerup 没落到本元素上），必须先清干净：否则下一次触摸
    // 会和一个幽灵点凑成"两指"，画面凭空乱缩一下。
    if (e.isPrimary) { ptsRef.current.clear(); pinchRef.current = null; movedRef.current = false; }
    // 连按下点一起记（x0/y0）：位移判定必须**先于** kind/zoom 的任何分支，
    // 否则 100% 时（以及视频态）划一下会被当成"点空白"而关掉弹窗（D6）。
    ptsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY });
    if (kind !== 'image') return;
    if (ptsRef.current.size >= 2) {
      // 第二根手指落下 ⇒ 捏合接管，单指拖拽让位（否则两条路径同时写 pan，图会抖）。
      dragRef.current = null;
      const g = twoFingerGesture();
      pinchRef.current = syncPinch({
        prev: pinchRef.current, ids: g?.ids, gesture: g?.gesture, view: viewRef.current,
      });
      if (pinchRef.current) for (const id of ptsRef.current.keys()) capture(e.currentTarget, id);
      return;
    }
    if (viewRef.current.zoom <= 1) return;   // 未放大时单指不拖：留给"点空白处关闭"
    dragRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, pan: viewRef.current.pan };
    capture(e.currentTarget, e.pointerId);
  };

  const onPointerMove = (e) => {
    // 只统计"按下中"的指针（Map 只在 pointerdown 收、pointerup 删），鼠标悬停
    // 划过不算。任一按下中的指针离开落点超过容差 ⇒ 这一下是划动不是点击，
    // 与 zoom 是否 >1、是不是两指全都无关（D6 的修复点就在这一句的位置）。
    const p = ptsRef.current.get(e.pointerId);
    if (p) {
      const next = { ...p, x: e.clientX, y: e.clientY };
      ptsRef.current.set(e.pointerId, next);
      if (exceedsTapSlop(next, { x: next.x0, y: next.y0 })) movedRef.current = true;
    }
    if (pinchRef.current && ptsRef.current.size >= 2) {
      const g = twoFingerGesture();
      // 每一帧都重新核对指对身份，而不是只在 pointerdown 拍一次快照：三指按住时
      // 抬走前两根之一，指针数仍是 2（下面 endDrag 的"跌破两指"收尾够不着），
      // 但手势指对已经换了人。syncPinch 察觉换人就地重新起手，这一帧因此是恒等
      // 变换；旧写法会拿另一对的指距去除以这一对的起手指距 —— 实测 1px 的位移
      // 换来 +28% 缩放与 141px 平移（D-A）。
      const pinch = syncPinch({
        prev: pinchRef.current, ids: g?.ids, gesture: g?.gesture, view: viewRef.current,
      });
      pinchRef.current = pinch;
      if (pinch && g) {
        movedRef.current = true;
        commitView(pinchView({
          start: pinch.start, current: g.gesture, startView: pinch.startView, ...viewport(),
        }));
      }
      return;
    }
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    movedRef.current = true;
    const raw = { x: d.pan.x + (e.clientX - d.x), y: d.pan.y + (e.clientY - d.y) };
    commitView((cur) => ({ ...cur, pan: clampPan(raw, { zoom: cur.zoom, ...viewport() }) }));
  };

  const endDrag = (e) => {
    ptsRef.current.delete(e.pointerId);
    // 还剩 ≥2 根 ⇒ 捏合继续，但指对可能已经换人；不在这里重新起手（此刻还没有
    // 新指对的坐标事件），交给下一次 pointermove 的 syncPinch —— 期间不会画任何
    // 一帧，所以看不见跳变。
    if (ptsRef.current.size < 2 && pinchRef.current) {
      pinchRef.current = null;
      // 捏合收尾时若还剩一根手指，就地把它接成拖拽的起点——不然剩下那根手指
      // 继续滑动会以"上一次 pointerdown 的位置"为基准，图猛地跳一下。
      const [rest] = [...ptsRef.current.entries()];
      const cur = viewRef.current;
      if (rest && cur.zoom > 1) dragRef.current = { id: rest[0], x: rest[1].x, y: rest[1].y, pan: cur.pan };
      return;
    }
    if (dragRef.current?.id === e.pointerId) dragRef.current = null;
  };

  // 全屏 Dialog 没有可点的遮罩：图片外的空白等价于遮罩，点一下就关。
  // 三道闸都必要：点的确实是空白（不是图/播放器）、当前没放大（放大后那片空白是
  // 拖拽/捏合的收尾区）、这一下没挪过（捏回 100% 后松手不该被当成"点了空白"）。
  const onBackdropClick = (e) => {
    if (e.target === boxRef.current && isIdentityView(viewRef.current) && !movedRef.current) onClose?.();
  };

  const zoomed = !isIdentityView(view);
  const media = kind === 'video' ? (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video src={src} controls style={{ maxWidth: '100%', maxHeight: '100%' }} />
  ) : (
    <Box
      component="img"
      src={src}
      alt={alt || ''}
      draggable={false}
      sx={(theme) => ({
        maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', userSelect: 'none',
        transform: `translate3d(${view.pan.x}px, ${view.pan.y}px, 0) scale(${view.zoom})`,
        transformOrigin: 'center center',
        // 捏合期间也必须关掉过渡：每帧都在改 transform，补间会让手指与画面脱节。
        transition: (dragRef.current || pinchRef.current) ? 'none' : 'transform 120ms ease-out',
        cursor: view.zoom > 1 ? 'grab' : 'zoom-in',
        // 透明区条纹：本组件的 <img> 渲染进 Dialog 的 portal，正文那条
        // `.dc-hint-on img.doc-asset--image` 够不着它，原先挂的 `dc-alpha-hint`
        // 又全仓没有对应 CSS —— 按钮写着"提示：开"，画面上什么也不会发生（D-B）。
        // 现在就画在自己身上，样式与正文同源（alphaHintSx，只有选择器不同）。
        // 位图按 object-fit: contain 在**自适应尺寸**的框里显示（未设 width/height，
        // 框就是图的比例），所以背景不会溢出成信箱黑边上的条纹。
        ...(hint ? alphaHintSx(theme.palette.mode) : null),
      })}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Stack sx={{ height: '100%', bgcolor: 'background.default' }}>
        <Box sx={{ px: { xs: 1, sm: 2 }, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          {/* 第一行只有两个孩子：标题（可缩）+ 关闭（不可缩）。逃生按钮不参与
              "挤不下就换行"的博弈，任何宽度下都在屏内——这正是 S1 的修复点。 */}
          <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1}>
            <Typography
              variant="body2"
              title={alt || undefined}
              sx={{
                flexGrow: 1, minWidth: 0, wordBreak: 'break-word',
                // 触屏读不到 title= 提示，所以窄屏放到两行再截，而不是一行省略号。
                display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
                overflow: 'hidden',
              }}
            >
              {alt || t('assetView.untitled')}
            </Typography>
            <Button size="small" variant="outlined" onClick={onClose} sx={{ flexShrink: 0 }}>
              {t('common.close')}
            </Button>
          </Stack>
          <Stack {...LIGHTBOX_TOOLS_PROPS}>
            {kind === 'image' ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44 }}>
                  {Math.round(view.zoom * 100)}%
                </Typography>
                <Tooltip title={t('assetView.zoomOut')}>
                  <span>
                    <IconButton size="small" onClick={() => applyZoom(-1)} disabled={view.zoom <= 1}>
                      <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 18, lineHeight: 1 }}>−</Box>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('assetView.zoomIn')}>
                  <span>
                    <IconButton size="small" onClick={() => applyZoom(+1)}>
                      <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 18, lineHeight: 1 }}>＋</Box>
                    </IconButton>
                  </span>
                </Tooltip>
                <Button size="small" onClick={() => commitView(IDENTITY_VIEW)} disabled={!zoomed}>
                  {t('assetView.reset')}
                </Button>
                <Button size="small" onClick={onToggleHint}>
                  {hint ? t('assetView.hintOn') : t('assetView.hintOff')}
                </Button>
              </>
            ) : null}
            <Button size="small" href={src} target="_blank" rel="noopener">
              {t('assetView.download')}
            </Button>
          </Stack>
        </Box>
        <Box
          ref={attachBox}
          onClick={onBackdropClick}
          onDoubleClick={onDoubleClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          sx={{
            flexGrow: 1, minHeight: 0, display: 'flex', alignItems: 'center',
            // touchAction:'none' 是**故意**保留的：两指捏合由 pinchView 自己实现，
            // 必须先拿到 pointermove，浏览器不能把手势抢去滚页面（理由见 assetView.mjs）。
            justifyContent: 'center', overflow: 'hidden', p: 2, touchAction: 'none',
          }}
        >
          {media}
        </Box>
        {kind === 'image' ? (
          // 两句帮助按输入方式分流：手机上讲滚轮是噪音，桌面上讲捏合也是。
          // 用 CSS 断点切换而不是 JS 媒体查询——这里没有任何需要 JS 才知道的东西。
          <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {t('assetView.help')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
              {t('assetView.helpTouch')}
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </Dialog>
  );
}
